import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { FiColumns } from "react-icons/fi";

const ColumnToggleButton = styled.button`
  background: ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#f8f9fa")};
  color: ${({ isDarkMode }) => (isDarkMode ? "#f3f4f6" : "#4b5563")};
  border: 1px solid ${({ isDarkMode }) => (isDarkMode ? "#4b5563" : "#e5e7eb")};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s;

  &:hover {
    background: ${({ isDarkMode }) => (isDarkMode ? "#4b5563" : "#e5e7eb")};
    border-color: ${({ isDarkMode }) => (isDarkMode ? "#6b7280" : "#d1d5db")};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: ${({ isDarkMode }) => (isDarkMode ? "#1f2937" : "white")};
  border: 1px solid ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#e5e7eb")};
  border-radius: 8px;
  padding: 1rem;
  min-width: 200px;
  box-shadow: 0 4px 12px
    rgba(0, 0, 0, ${({ isDarkMode }) => (isDarkMode ? "0.5" : "0.1")});
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid
    ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#e5e7eb")};
`;

const DropdownTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ isDarkMode }) => (isDarkMode ? "#f3f4f6" : "#111827")};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ isDarkMode }) => (isDarkMode ? "#9ca3af" : "#6b7280")};
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#f3f4f6")};
  }
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#f9fafb")};
  }
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ isDarkMode }) => (isDarkMode ? "#667eea" : "#4f46e5")};
`;

const CheckboxLabel = styled.span`
  font-size: 0.9rem;
  color: ${({ isDarkMode }) => (isDarkMode ? "#e5e7eb" : "#374151")};
  flex: 1;
`;

const DropdownFooter = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid
    ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#e5e7eb")};
`;

const FooterButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid ${({ isDarkMode }) => (isDarkMode ? "#4b5563" : "#d1d5db")};
  border-radius: 4px;
  background: ${({ isDarkMode }) => (isDarkMode ? "#374151" : "#f9fafb")};
  color: ${({ isDarkMode }) => (isDarkMode ? "#e5e7eb" : "#4b5563")};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ isDarkMode }) => (isDarkMode ? "#4b5563" : "#e5e7eb")};
  }

  &.primary {
    background: ${({ isDarkMode }) => (isDarkMode ? "#667eea" : "#4f46e5")};
    color: white;
    border-color: ${({ isDarkMode }) => (isDarkMode ? "#764ba2" : "#4338ca")};

    &:hover {
      background: ${({ isDarkMode }) => (isDarkMode ? "#764ba2" : "#4338ca")};
    }
  }
`;

const ColumnToggle = ({
  tableHeaders,
  visibleColumns,
  onVisibleColumnsChange,
  isDarkMode,
  position = "right",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localVisibleColumns, setLocalVisibleColumns] =
    useState(visibleColumns);
  const dropdownRef = useRef(null);

  // Initialize local state when props change
  useEffect(() => {
    setLocalVisibleColumns(visibleColumns);
  }, [visibleColumns]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckboxChange = (key) => {
    setLocalVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleApply = () => {
    onVisibleColumnsChange(localVisibleColumns);
    setIsOpen(false);
  };

  const handleReset = () => {
    const allVisible = {};
    tableHeaders.forEach((header) => {
      allVisible[header.key] = true;
    });
    setLocalVisibleColumns(allVisible);
    onVisibleColumnsChange(allVisible);
  };

  const handleSelectAll = () => {
    const allSelected = {};
    tableHeaders.forEach((header) => {
      allSelected[header.key] = true;
    });
    setLocalVisibleColumns(allSelected);
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    tableHeaders.forEach((header) => {
      noneSelected[header.key] = false;
    });
    setLocalVisibleColumns(noneSelected);
  };

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <ColumnToggleButton
        onClick={() => setIsOpen(!isOpen)}
        isDarkMode={isDarkMode}
      >
        <FiColumns />
        Columns
      </ColumnToggleButton>

      {isOpen && (
        <DropdownMenu isDarkMode={isDarkMode} style={{ [position]: 0 }}>
          <DropdownHeader isDarkMode={isDarkMode}>
            <DropdownTitle isDarkMode={isDarkMode}>
              Show/Hide Columns
            </DropdownTitle>
            <CloseButton
              onClick={() => setIsOpen(false)}
              isDarkMode={isDarkMode}
            >
              ✕
            </CloseButton>
          </DropdownHeader>

          <CheckboxList>
            {tableHeaders.map((header) => (
              <CheckboxItem key={header.key} isDarkMode={isDarkMode}>
                <CheckboxInput
                  type="checkbox"
                  checked={localVisibleColumns[header.key] || false}
                  onChange={() => handleCheckboxChange(header.key)}
                  isDarkMode={isDarkMode}
                />
                <CheckboxLabel isDarkMode={isDarkMode}>
                  {header.label}
                </CheckboxLabel>
              </CheckboxItem>
            ))}
          </CheckboxList>

          <DropdownFooter>
            <FooterButton onClick={handleSelectAll} isDarkMode={isDarkMode}>
              All
            </FooterButton>
            <FooterButton onClick={handleDeselectAll} isDarkMode={isDarkMode}>
              None
            </FooterButton>
            <FooterButton onClick={handleReset} isDarkMode={isDarkMode}>
              Reset
            </FooterButton>
            <FooterButton
              onClick={handleApply}
              className="primary"
              isDarkMode={isDarkMode}
            >
              Apply
            </FooterButton>
          </DropdownFooter>
        </DropdownMenu>
      )}
    </div>
  );
};

export default ColumnToggle;
